/* eslint-disable no-console */
import {Navigate, useLocation} from 'react-router-dom';
import {useAuthContext} from '../context/AuthContext';
import {PRIVATE_ROUTES, PUBLIC_ROUTES} from './RouteConstants';
import AuthUtils from 'src/utils/AuthUtils';
import FormConstants from '@shared/constants/FormConstants';
import {BASE_ROUTE} from './BaseRoute';
import {useEffect, useMemo} from 'react';
import {setSession} from 'src/auth/context/jwt/utils';

const athenaRoutes = [
  `/${BASE_ROUTE}/${PUBLIC_ROUTES.ATHENA}`,
  `/${BASE_ROUTE}/${PUBLIC_ROUTES.ATHENA_EXTERNAL}`
];

export function AuthenticatedRoute({
  children
}: {
  children: JSX.Element;
}): JSX.Element {
  const location = useLocation();
  const {authState, logout} = useAuthContext();
  const {isAuthenticated} = authState;

  // Check if the AuthToken is valid
  const hasValidAuthToken = useMemo(() => {
    const token = AuthUtils.getAuthToken();
    return token && token.length > 0;
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !hasValidAuthToken) {
      logout();
      setSession(null);
    }
  }, [hasValidAuthToken, isAuthenticated, logout]);

  // Redirect to login if AuthToken is missing or invalid
  if (!isAuthenticated || !hasValidAuthToken) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return (
      <Navigate
        to={PUBLIC_ROUTES.ROOT_DEFAULT + '/' + PUBLIC_ROUTES.LOGIN}
        state={{from: location.pathname}}
      />
    );
  }

  let permissionExisted = false,
    selectedRoute = '';
  const permissions = AuthUtils.getPermissions();

  if (location?.pathname) {
    const routesList = location?.pathname?.split('/');
    selectedRoute =
      routesList?.length > 4
        ? routesList?.[routesList?.length - 3]
        : routesList?.[routesList?.length - 1];

    selectedRoute =
      selectedRoute === FormConstants.MODULE.PURGE_AND_LEGAL_HOLD
        ? FormConstants.MODULE.PURGE
        : selectedRoute;

    selectedRoute =
      selectedRoute === FormConstants.MODULE.AUDIT_LOGS
        ? FormConstants.MODULE.USER_ACCESS_LOGS
        : selectedRoute;

    if (selectedRoute && permissions?.length > 0) {
      permissionExisted = permissions?.some(
        (ele: any) => ele.object === selectedRoute
      );
    }
  }

  if (
    permissionExisted ||
    selectedRoute === PRIVATE_ROUTES.DASHBOARD ||
    selectedRoute === PRIVATE_ROUTES.MODULE ||
    selectedRoute === PRIVATE_ROUTES.SUB_MODULE ||
    selectedRoute === PRIVATE_ROUTES.LMS ||
    selectedRoute === PRIVATE_ROUTES.UAM
  ) {
    return children;
  } else {
    AuthUtils.setRedirectRoute(PRIVATE_ROUTES.UNAUTHORIZED_USER);
    return <Navigate to={`/${BASE_ROUTE}/${PRIVATE_ROUTES.DASHBOARD}`} />;
  }
}

export function UnauthenticatedRoute({
  children
}: {
  children: JSX.Element;
}): JSX.Element {
  const location = useLocation();
  const {authState} = useAuthContext();
  const {isAuthenticated} = authState;

  if (isAuthenticated && !athenaRoutes.includes(location?.pathname)) {
    // Redirect them to the /dashboard page
    return (
      <Navigate
        to={`/${BASE_ROUTE}/${PRIVATE_ROUTES.DASHBOARD}`}
        state={{from: location.pathname}}
      />
    );
  }

  return children;
}

// For routes which do not depend on any state
export function GuestRoute({children}: {children: JSX.Element}): JSX.Element {
  return children;
}
